import { useMediaQuery } from 'react-responsive';
import { type SupportedLocale } from '@zep-us/i18n';
import { Trans, useTranslation } from 'next-i18next';
import { NoSSR } from '@/components';
import { Breakpoints, useLocale, useViewport } from '@/hooks';
import { useCountryCode } from '@/shared/hooks';
import { FunctionCard, type FunctionCardProps } from './FunctionCard';

/**
 * `id` 카드를 `afterId` 카드 바로 뒤로 옮긴 새 배열. 둘 중 하나라도 없으면 원본 그대로.
 * (국가별 필터로 카드가 빠질 수 있어 못 찾는 경우를 정상으로 본다.)
 */
export const moveCardAfter = (cards: FunctionCardProps[], id: string, afterId: string) => {
  const moved = cards.find(card => card.id === id);
  if (!moved) return cards;
  const rest = cards.filter(card => card.id !== id);
  const anchor = rest.findIndex(card => card.id === afterId);
  if (anchor < 0) return cards;
  return [...rest.slice(0, anchor + 1), moved, ...rest.slice(anchor + 1)];
};
export const PricingSection2 = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <section className="flex flex-col items-center gap-xl px-md py-2xl mobile-lg:px-lg tablet-lg:px-2xl tablet-lg:py-3xl" data-sentry-component="PricingSection2" data-sentry-source-file="PricingSection2.tsx">
      <h2 className="whitespace-pre-line text-center text-display-sm-extrabold">
        <Trans t={t} i18nKey="pricing:functionCards.title" components={{
        Gradient: <strong className="bg-clip-text text-transparent bg-gradient-violet-blue" />
      }} data-sentry-element="Trans" data-sentry-source-file="PricingSection2.tsx" />
      </h2>
      <div className="grid w-full max-w-[1280px] grid-cols-1 gap-md tablet-lg:grid-cols-2 laptop-sm:grid-cols-6 laptop-sm:gap-lg">
        <FunctionCards data-sentry-element="FunctionCards" data-sentry-source-file="PricingSection2.tsx" />
        <MoreCard data-sentry-element="MoreCard" data-sentry-source-file="PricingSection2.tsx" />
      </div>
    </section>;
};
export const FunctionCards = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    locale
  } = useLocale();
  const {
    countryCode
  } = useCountryCode();
  const {
    isPortable,
    isLaptopSm
  } = useViewport();
  // tailwind `laptop-sm:`(min-width 1280px)와 정확히 같은 질의. 이 지점에서 그리드가
  // 6열로 펼쳐지고, 전폭 카드인 ZEP BOARD 의 자리도 함께 바뀐다.
  const isSixColumnGrid = useMediaQuery({
    query: `(min-width: ${Breakpoints.laptopSm}px)`
  });
  const getImagePath = (filename: string) => {
    const AVAILABLE_LOCALES: SupportedLocale[] = ['ko', 'en', 'ja'];
    const localeKey = AVAILABLE_LOCALES.includes(locale) ? locale : 'en';
    return `/assets/pricing/${filename.replace('{locale}', localeKey)}`;
  };
  const CARDS_DATA: FunctionCardProps[] = [{
    id: 'premium-maps',
    layout: isPortable ? 'vertical' : 'horizontal',
    className: 'col-span-1 tablet-lg:col-span-2 laptop-sm:col-span-6 bg-green-50',
    title: t('pricing:premiumMaps.title'),
    description: t('pricing:premiumMaps.description'),
    imgSrc: 'function_premium_maps_language_all.webp'
  }, {
    id: 'quiz-ai',
    layout: isPortable || isLaptopSm ? 'vertical' : 'horizontal',
    className: 'col-span-1 laptop-sm:col-span-6 bg-blue-50',
    title: t('pricing:quizAi.title'),
    description: t('pricing:quizAi.description'),
    imgSrc: 'function_quiz_ai_language_{locale}.webp'
  }, {
    id: 'learning-report',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-red-50',
    title: t('pricing:learningReport.title'),
    description: t('pricing:learningReport.description'),
    imgSrc: 'function_learning_report_language_{locale}.webp'
  }, {
    id: 'ai-tutor',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-yellow-50',
    title: t('pricing:aiTutor.title'),
    description: t('pricing:aiTutor.description'),
    imgSrc: 'function_ai_tutor_language_all.webp',
    visibleCountries: ['KR']
  }, {
    id: 'ai-translation',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-3 bg-yellow-50',
    title: t('pricing:aiTranslation.title'),
    description: t('pricing:aiTranslation.description'),
    imgSrc: 'function_ai_translation_language_all.webp',
    hideCountries: ['KR']
  }, {
    id: 'zep-board',
    // 프리미엄 퀴즈 맵과 같은 전폭 카드라 세로 전환 시점도 같다 — 1열이 되는
    // 휴대 기기에서만 세로다. 1024~1280 에서 세로로 두면 415px 캡 때문에
    // 전폭 카드 안에서 이미지가 시안보다 훨씬 작아 보인다.
    layout: isPortable ? 'vertical' : 'horizontal',
    className: 'col-span-1 tablet-lg:col-span-2 laptop-sm:col-span-6 bg-violet-50',
    title: t('pricing:zepBoard.title'),
    description: t('pricing:zepBoard.description'),
    imgSrc: 'function_zep_board_language_{locale}.webp'
  }, {
    id: 'lock',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:quizLock.title'),
    description: t('pricing:quizLock.description'),
    imgSrc: 'function_lock_language_{locale}.webp'
  }, {
    id: 'worksheet',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:worksheet.title'),
    description: t('pricing:worksheet.description'),
    imgSrc: 'function_worksheet_language_{locale}.webp'
  }, {
    id: 'next-quiz',
    layout: 'vertical',
    className: 'col-span-1 laptop-sm:col-span-2 bg-neutral',
    title: t('pricing:nextQuiz.title'),
    description: t('pricing:nextQuiz.description'),
    imgSrc: 'function_next_quiz_language_all.webp'
  }];
  const visibleCards = CARDS_DATA.filter(card => {
    if (card.visibleCountries) {
      return card.visibleCountries.includes(countryCode);
    }
    if (card.hideCountries) {
      return !card.hideCountries.includes(countryCode);
    }
    return true;
  });

  // 6열에선 ZEP BOARD 가 잠금 카드 앞에 오지만, 2열 이하에선 잠금 카드와 나란히 설
  // 수 없어 시안대로 그 뒤로 내려간다(AI 학습 도우미 → 비공개 설정 → ZEP BOARD).
  const cards = isSixColumnGrid ? visibleCards : moveCardAfter(visibleCards, 'zep-board', 'lock');
  return <NoSSR data-sentry-element="NoSSR" data-sentry-component="FunctionCards" data-sentry-source-file="PricingSection2.tsx">
      {cards.map(card => card.layout === 'horizontal' ? <FunctionCard.Horizontal key={card.id} className={card.className} title={card.title} description={card.description} imgSrc={getImagePath(card.imgSrc)} /> : <FunctionCard.Vertical key={card.id} className={card.className} title={card.title} description={card.description} imgSrc={getImagePath(card.imgSrc)} />)}
    </NoSSR>;
};
const MoreCard = () => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  return <article className="col-span-1 flex flex-col items-center gap-md rounded-lg border border-alternative bg-neutral p-2xl tablet-lg:col-span-2 laptop-sm:col-span-6" data-sentry-component="MoreCard" data-sentry-source-file="PricingSection2.tsx">
      <h5 className="text-heading-xl-extrabold text-alternative">
        {t('pricing:home.pricing.moreFunctionComing')}
      </h5>
      <p className="text-body-lg-semibold text-alternative">
        {t('pricing:moreComingSoon')}
      </p>
    </article>;
};